import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Flex,
  Switch,
  Text,
  Button,
  Box,
  TextField,
  Callout,
} from "@radix-ui/themes";
import { toast } from "sonner";
import {
  useGetBusinessHours,
  useUpdateBusinessHours,
} from "@/api/business-hours/BusinessHours.api";
import { useGetAccess } from "@/api/useAccess";
import Checkbox from "@/components/shared/inputs/checkbox/Checkbox";
import styles from "./BusinessHours.module.scss";
import { InfoCircledIcon } from "@radix-ui/react-icons";

interface BusinessHoursRule {
  dayOfWeek: number;
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  enabled: boolean;
}

interface BusinessHoursConfig {
  enabled: boolean;
  rules: BusinessHoursRule[];
}

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

// Helper functions for time formatting
const formatTime = (hour: number, minute: number) =>
  `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;

const parseTime = (timeString: string) => {
  const [hour, minute] = timeString.split(":").map(Number);
  return { hour: hour || 0, minute: minute || 0 };
};

export function BusinessHours() {
  const { user } = useAuth0();
  const { data: access } = useGetAccess(!!user);
  const {
    data: businessHours,
    isLoading,
    refetch,
  } = useGetBusinessHours(!!user);
  const updateBusinessHours = useUpdateBusinessHours();

  const [config, setConfig] = useState<BusinessHoursConfig>({
    enabled: false,
    rules: DAYS_OF_WEEK.map((day) => ({
      dayOfWeek: day.value,
      startHour: 9,
      startMinute: 0,
      endHour: 17,
      endMinute: 0,
      enabled: day.value >= 1 && day.value <= 5, // Monday to Friday by default
    })),
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (businessHours) {
      setConfig({
        enabled: businessHours.enabled || false,
        rules:
          businessHours.rules?.map((rule) => ({
            ...rule,
            startMinute: rule.startMinute ?? 0, // Default to 0 if not present (backward compatibility)
            endMinute: rule.endMinute ?? 0, // Default to 0 if not present (backward compatibility)
          })) ||
          DAYS_OF_WEEK.map((day) => ({
            dayOfWeek: day.value,
            startHour: 9,
            startMinute: 0,
            endHour: 17,
            endMinute: 0,
            enabled: day.value >= 1 && day.value <= 5,
          })),
      });
      setHasChanges(false);
    }
  }, [businessHours]);

  const handleEnabledChange = useCallback((enabled: boolean) => {
    setConfig((prev) => ({ ...prev, enabled }));
    setHasChanges(true);
  }, []);

  const handleRuleChange = useCallback(
    (
      dayOfWeek: number,
      field: keyof BusinessHoursRule,
      value: boolean | number
    ) => {
      setConfig((prev) => ({
        ...prev,
        rules: prev.rules.map((rule) =>
          rule.dayOfWeek === dayOfWeek ? { ...rule, [field]: value } : rule
        ),
      }));
      setHasChanges(true);
    },
    []
  );

  const handleTimeChange = useCallback(
    (dayOfWeek: number, timeType: "start" | "end", timeString: string) => {
      const { hour, minute } = parseTime(timeString);
      setConfig((prev) => ({
        ...prev,
        rules: prev.rules.map((rule) =>
          rule.dayOfWeek === dayOfWeek
            ? {
                ...rule,
                [`${timeType}Hour`]: hour,
                [`${timeType}Minute`]: minute,
              }
            : rule
        ),
      }));
      setHasChanges(true);
    },
    []
  );

  const handleSave = async () => {
    try {
      await updateBusinessHours.mutateAsync(config);
      toast.success("Business hours updated successfully");
      setHasChanges(false);
      refetch();
    } catch (error) {
      toast.error("Failed to update business hours");
      console.error("Error updating business hours:", error);
    }
  };

  const handleCancel = () => {
    if (businessHours) {
      setConfig({
        enabled: businessHours.enabled || false,
        rules:
          businessHours.rules &&
          businessHours.rules.length === DAYS_OF_WEEK.length
            ? businessHours.rules.map((rule, idx) => ({
                dayOfWeek: rule.dayOfWeek,
                startHour: rule.startHour ?? 9,
                startMinute: rule.startMinute ?? 0,
                endHour: rule.endHour ?? 17,
                endMinute: rule.endMinute ?? 0,
                enabled:
                  typeof rule.enabled === "boolean"
                    ? rule.enabled
                    : rule.dayOfWeek >= 1 && rule.dayOfWeek <= 5,
              }))
            : DAYS_OF_WEEK.map((day) => ({
                dayOfWeek: day.value,
                startHour: 9,
                startMinute: 0,
                endHour: 17,
                endMinute: 0,
                enabled: day.value >= 1 && day.value <= 5,
              })),
      });
    }
    setHasChanges(false);
  };

  if (!access?.isAdmin) {
    return (
      <Flex justify="center" py="8">
        <Text size="2" color="gray">
          You don't have permission to access this page.
        </Text>
      </Flex>
    );
  }

  if (isLoading) {
    return (
      <Flex justify="center" py="8">
        <Text size="2" color="gray">
          Loading...
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="24px">
      <div className={styles.PageHeader}>
        <div className={styles.Title}>Business Hours</div>
        <div className={styles.Description}>
          Configure when Quack should respond to customer inquiries. By default,
          business hours are disabled and Quack responds 24/7. When enabled, you
          can set specific hours for each day of the week in UTC timezone.
        </div>
        <Callout.Root color="blue">
          <Callout.Icon>
            <InfoCircledIcon />
          </Callout.Icon>
          <Callout.Text>
            Business hours are just for Tickets AI Agent.
          </Callout.Text>
        </Callout.Root>
      </div>

      <Box className={styles.ConfigSection}>
        <Flex align="center" gap="3" mb="4">
          <Switch
            checked={config.enabled}
            onCheckedChange={handleEnabledChange}
            highContrast
          />
          <Text size="3" weight="medium">
            Enable Business Hours
          </Text>
        </Flex>

        {config.enabled && (
          <div className={styles.RulesSection}>
            <Text size="2" color="gray" mb="3">
              Configure business hours for each day of the week (UTC timezone):
            </Text>

            {DAYS_OF_WEEK.map((day) => {
              const rule = config.rules.find((r) => r.dayOfWeek === day.value);
              if (!rule) return null;

              return (
                <div key={day.value} className={styles.DayRule}>
                  <Flex align="center" gap="3">
                    <div className={styles.DayCheckbox}>
                      <Checkbox
                        label={day.label}
                        value={rule.enabled}
                        onChange={(enabled) =>
                          handleRuleChange(day.value, "enabled", enabled)
                        }
                      />
                    </div>

                    {rule.enabled && (
                      <Flex align="center" gap="2">
                        <TextField.Root
                          type="time"
                          value={formatTime(rule.startHour, rule.startMinute)}
                          onChange={(e) =>
                            handleTimeChange(day.value, "start", e.target.value)
                          }
                          className={styles.TimeInput}
                          size="2"
                        />
                        <Text size="2" color="gray">
                          to
                        </Text>
                        <TextField.Root
                          type="time"
                          value={formatTime(rule.endHour, rule.endMinute)}
                          onChange={(e) =>
                            handleTimeChange(day.value, "end", e.target.value)
                          }
                          className={styles.TimeInput}
                          size="2"
                        />
                      </Flex>
                    )}
                  </Flex>
                </div>
              );
            })}
          </div>
        )}

        <Flex gap="3" mt="4" justify="end">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={updateBusinessHours.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateBusinessHours.isLoading}
            highContrast
          >
            {updateBusinessHours.isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Flex>
      </Box>
    </Flex>
  );
}
